import {Component, OnInit, Input, AfterViewInit, ViewContainerRef, ViewChild} from '@angular/core';
import {fabric} from 'fabric';
import {HttpClient} from '@angular/common/http';
import {ColorPickerService, Cmyk} from 'ngx-color-picker';
import {CropperComponent} from 'angular-cropperjs';
import {CommonService} from '../../services/common.service';
import {CanvasService} from '../../services/canvas.service';
import {HttpService} from '../../services/http-service.service';
import {PlaceDetail} from '../../model/places';

@Component({
  selector: 'core-location-map',
  templateUrl: './location-map.component.html',
  styleUrls: ['./location-map.component.scss']
})

export class LocationMapComponent implements OnInit, AfterViewInit {

  @Input() locationInfo;
  @Input() index;
  @Input() canvasJson;
  @Input() isEditMode;
  @Input() imageData: string = null;

  @ViewChild('angularCropper1', {static: true}) angularCropper: CropperComponent;

  // Map canvas variables
  latitude: number = 0;
  longitude: number = 0;
  canvas: any;
  mapview: string = 'hybrid';
  zoomLevel: number = 16;
  imageWidth: number = 640;
  imageHeight: number = 500;
  imageSize: string = this.imageWidth + 'x' + this.imageHeight;
  shapeBorderColor: string = '#000000';
  shapeStroke: string = '#FFFF00';
  shapeFillColor: string = 'rgba(0,0,0,1)';
  shapeStrokeWidth: number = 3;
  stateName: string = '';
  isCropMode: boolean = false;
  staticGmapUrl: string = '';
  cropConfig = {
    dragMode: 'move',
    background: false,
    minContainerWidth: 600,
    minContainerHeight: 428,
    movable: true,
    rotatable: true,
    scalable: true,
    zoomable: true,
    viewMode: 3,
    cropmove: this.cropMoved.bind(this),
    cropBoxResizable: false
  };
  croppedImage: any = '';
  searchValue: string = '';
  locateView: google.maps.Map;
  isShowAnimation = true;
  mapImage: string = '';
  mapLoading: boolean = false;

  // Marker variable (Search Place)
  placesList: PlaceDetail[] = [];
  searchBox: google.maps.places.SearchBox;
  markers = [];
  showSearchloading: boolean = false;
  markerParam: string = '';
  mode: string = 'draw';

  constructor(
    public httpClient: HttpClient,
    public httpService: HttpService,
    public vcRef: ViewContainerRef,
    private cpService: ColorPickerService,
    public commonService: CommonService,
    public canvasService: CanvasService,
  ) {
  }

  ngOnInit(): void {
    this.commonService.states.forEach(el => {
      if (el.value === this.locationInfo.STATE) {
        this.stateName = el.name;
      }
    });
  }

  ngAfterViewInit(): void {
    this.canvas = new fabric.Canvas('mapCanvas' + this.index);
    this.getCoordinates();
  }

  ngOnChanges(change): void {
    if (change.canvasJson && change.canvasJson.currentValue) {
      this.canvas.loadFromJSON(change.canvasJson.currentValue, function () {
        if (this) {
          this.canvas.renderAll();
        }
      }, function () {
      });
    }
    if (change.isEditMode) {
      this.isEditMode = change.isEditMode.currentValue;
      if (this.canvas) {
        this.canvas.loadFromJSON(this.canvas.toJSON(), this.canvas.renderAll.bind(this.canvas), function (o, object) {
          if (object.type == 'image') {
            object.set('selectable', false);
          } else {
            object.set('selectable', change.isEditMode.currentValue);
          }
        });
      }
    }
  }

  // getCoordinates() - get latitude and longitude for given location
  getCoordinates(): void {
    let requestUrl = this.locationInfo.ADDRESS + ','
      + this.locationInfo.CITY + ','
      + this.locationInfo.STATE + ' '
      + this.locationInfo.ZIP + ',USA';

    this.mapLoading = true;
    this.httpService.getGeocode(requestUrl).subscribe((resp) => {
      if (resp.success) {
        if (resp['result'].results.length > 0) {
          this.latitude = resp['result'].results[0].geometry.location.lat;
          this.longitude = resp['result'].results[0].geometry.location.lng;
          if(this.imageData){
            this.loadSavedImage();
          }else {
            this.mapLocation(false);
          }
          this.findPlacesInCity();
        }
      }
      this.mapLoading = false;
    }, (error) => {
      if (error) {
        this.mapLoading = false;
        // this.toastrService.error(error.message);
      }
    });
  }

  // mapLocation() - Get google map location and load it into canvas on the basis of user entered details
  mapLocation(isChangeView: boolean): void {

    // Get image of that location from google map
    let paramString = 'center=' + this.latitude + ',' + this.longitude
      + '&zoom=' + this.zoomLevel
      + '&scale=1'
      + '&size=' + this.imageSize
      + '&sensor=false'
      + '&maptype=' + this.mapview;
    if (this.markerParam) {
      paramString += this.markerParam;
    }

    this.mapLoading = true;

    this.httpService.getMapImage(paramString).subscribe((resp) => {
      this.mapImage = this.commonService._imageEncode(resp);
      // Load image of that location into canvas
      fabric.Image.fromURL(this.mapImage, (image) => {
        image.set({left: 0, top: 0});

        // If we are changing view just change image else add new one
        if (isChangeView) {
          this.canvas.item(0).set(image);
          this.canvas.renderAll();
        } else this.canvas.add(image);
        this.canvas.item(0).lockMovementX = true;
        this.canvas.item(0).lockMovementY = true;
        this.canvas.item(0).selectable = false;

        this.mapLoading = false;
      }, {
        crossOrigin: 'anonymous'
      });
    }, (error) => {
      if (error) {
        this.mapLoading = false;
        // this.toastrService.error(error.message);
      }
    });

    // If image gets change den reinitalize cropper.
    if (isChangeView && this.angularCropper.cropper) {
      let testData1 = this.angularCropper.cropper;
      this.angularCropper.cropper.destroy();
      this.angularCropper.cropper = testData1;
    }
  }

  onChangeColorCmyk(color: string): Cmyk {
    const hsva = this.cpService.stringToHsva(color);

    if (hsva) {
      const rgba = this.cpService.hsvaToRgba(hsva);

      return this.cpService.rgbaToCmyk(rgba);
    }

    return new Cmyk(0, 0, 0, 0);
  }

  onChangeColorHex8(color: string): string {
    const hsva = this.cpService.stringToHsva(color, true);

    if (hsva) {
      return this.cpService.outputFormat(hsva, 'rgba', null);
    }

    return '';
  }

  // changeMapView() - Change view of the map, satellite or hybrid
  changeMapView(): void {
    this.mapview = this.mapview === 'HYBRID' ? 'hybrid' : this.mapview === 'hybrid' ? 'HYBRID' : 'hybrid';
    this.mapLocation(true);
  }

  // changeMode() - Change mode to locate or draw.
  changeMode(): void {
    if (this.isShowAnimation) this.isShowAnimation = false;

    if (this.mode === 'draw') {
      this.mode = 'locate';
      setTimeout(() => {
        this.locateView = new google.maps.Map(document.getElementById('locate-view' + this.index), {
          center: {lat: this.latitude, lng: this.longitude},
          zoom: this.zoomLevel,
          streetViewControl: false,
          fullscreenControl: false,
        });
      }, 500);
    } else {
      this.mode = 'draw';
      this.latitude = this.locateView.getCenter().lat();
      this.longitude = this.locateView.getCenter().lng();
      this.mapLocation(true);
    }
  }

  // selectShapeBorderWidth() - Select width of the border
  selectShapeBorderWidth(width: number): void {
    this.shapeStrokeWidth = width;
    if (this.canvas.getActiveObject()) {
      this.canvas.getActiveObject().set('strokeWidth', this.shapeStrokeWidth);
      this.canvas.renderAll();
    }
  }

  // cropAnImage() - Crop an image when user select crop area
  cropAnImage(): void {
    if (this.isCropMode) {
      if (this.croppedImage) {
        // Load image of that location into canvas
        fabric.Image.fromURL(encodeURI(this.croppedImage), (image) => {
          image.set({left: 0, top: 0});
          image.width = this.imageWidth;
          image.height = this.imageHeight;
          this.canvas.item(0).set(image);
          this.canvas.renderAll();

          this.canvas.item(0).lockMovementX = true;
          this.canvas.item(0).lockMovementY = true;
          this.canvas.item(0).selectable = false;
        }, {
          crossOrigin: 'anonymous'
        });
      }
    } else {
      this.croppedImage = '';
      this.angularCropper.cropper.setCropBoxData({top: 10, left: 10, height: 350, width: 380});
    }
    this.isCropMode = !this.isCropMode;
  }

  // cropMoved() - Create new image when user moves crop box
  cropMoved(): void {
    if (this.angularCropper.cropper.getCroppedCanvas()) {
      this.croppedImage = this.angularCropper.cropper.getCroppedCanvas().toDataURL();
    }
  }

  // zoomIn() - Zoom in on the map
  zoomIn(): void {
    this.zoomLevel = this.zoomLevel + 1;
    this.mapLocation(true);
  }

  // zoomOut() - Zoom out on the map
  zoomOut(): void {
    this.zoomLevel = this.zoomLevel - 1;
    this.mapLocation(true);
  }

  // moveLeft() - Move left
  moveLeft(): void {
    this.longitude = this.longitude - this.commonService.getValAccordingToZoomLevel(this.zoomLevel);
    this.mapLocation(true);
  }

  // moveRight() - Move right
  moveRight(): void {
    this.longitude = this.longitude + this.commonService.getValAccordingToZoomLevel(this.zoomLevel);
    this.mapLocation(true);
  }

  // moveTop() - Move top
  moveTop(): void {
    this.latitude = this.latitude + this.commonService.getValAccordingToZoomLevel(this.zoomLevel);
    this.mapLocation(true);
  }

  // moveDown() - Move down
  moveDown(): void {
    this.latitude = this.latitude - this.commonService.getValAccordingToZoomLevel(this.zoomLevel);
    this.mapLocation(true);
  }

  // findPlacesInCity() - Find places in the city.
  findPlacesInCity(): void {
    this.searchBox = new google.maps.places.SearchBox(document.getElementById('place-search' + this.index) as HTMLInputElement);

    // Bias the SearchBox results towards current map's viewport.
    this.searchBox.setBounds(new google.maps.LatLngBounds(new google.maps.LatLng({
      lat: this.latitude,
      lng: this.longitude
    })));

    // more details for that place.
    this.searchBox.addListener('places_changed', () => {
      this.showSearchloading = true;
      setTimeout(() => {
        // Clear out the old markers.
        this.markerParam = '';
        this.markers.forEach((marker) => {
          marker.setMap(null);
        });
        let list = this.searchBox.getPlaces();
        this.placesList = [];
        if (list && list.length > 0) {
          list.map((place) => {
            let placeDetail = new PlaceDetail();
            placeDetail.PLACE = place;
            placeDetail.IS_MARKED = true;
            this.placesList.push(placeDetail);
          });

          this.createMarkerParamString();
        }
        this.showSearchloading = false;
      }, 500);
    });
  }

  // createMarkerParamString() - Create string of all markers and get image of it.
  createMarkerParamString(): void {
    // Clear out the old markers.
    this.markers.forEach((marker) => {
      marker.setMap(null);
    });

    // Prepare param string for markers
    this.markerParam = '';
    this.placesList.map((place) => {
      if (place.IS_MARKED) {
        this.markerParam += '&markers=';

        let content = '';
        // if(place.IMAGE)
        //   content += 'icon:' + place.IMAGE + '|';
        content += place.PLACE.geometry.location.lat() + ',' + place.PLACE.geometry.location.lng();

        this.markerParam += btoa(content);
      }
    });

    // After creating marker string call API
    this.mapLocation(true);
  }

  // getPlaceDetailAndCreateMarker() - Get details about place and create marker on city.
  getPlaceDetailAndCreateMarker(placeIndex: number): void {
    this.placesList.map((place, index) => {
      if (index === placeIndex) {
        place.IS_MARKED = !place.IS_MARKED;
      }
    });

    // After getting details create marker on canvas.
    this.createMarkerParamString();
  }

  // preventClick() - Prevent click event of selecting row
  preventClick(event): void {
    event.stopPropagation();
  }

  // makeMapCenterForThisPlace() - Make map center according to this place.
  makeMapCenterForThisPlace(place: PlaceDetail): void {
    this.latitude = place.PLACE['geometry'].location.lat();
    this.longitude = place.PLACE['geometry'].location.lng();

    // After changing lat and lng load new map
    this.mapLocation(true);
  }

  /** Load Saved image */
  loadSavedImage(): void{
    // Load image of that location into canvas
    fabric.Image.fromURL(this.imageData, (image) => {
      image.set({left: 0, top: 0});

      // add new one
      this.canvas.add(image);
      this.canvas.item(0).lockMovementX = true;
      this.canvas.item(0).lockMovementY = true;
      this.canvas.item(0).selectable = false;

      this.mapLoading = false;
    }, {
      crossOrigin: 'anonymous'
    });
  }
}
